import useEntityAcrossSources from 'utils/hooks/useEntityAcrossSources';
import { TabButtonLink, TabList } from 'components/DataSourceTab';
import { useAbortController, useCurrentCountry } from 'utils/hooks';
import DimensionHelmet from 'components/DimensionHelmet';
import IfHasTikTokAccess from 'components/NoAccessMessage/IfHasTikTokAccess';
import InstagramBrand from './Instagram';
import OverviewBrand from './OverviewV2';
import PageBar from 'components/PageBar';
import { PagePadding } from 'components/PagePadding';
import SearchBrand from './Google';
import Stack from 'components/Stack';
import { TIKTOK_COUNTRIES } from '@/config/constants';
import TikTokBrand from './TikTok';
import useCountryParam from 'utils/hooks/useCountryParam';
import useDataSourceToggle from 'utils/hooks/useDataSourceToggle';
import { useParams } from 'react-router';

function Brand() {
    const { mutateToWithCountryParam } = useCountryParam();
    const { labels, mode, modes, dataSource } = useDataSourceToggle();
    const params = useParams();
    const { brandId } = params;
    const controller = useAbortController(brandId);
    // Source-independent: the page title describes the brand, not one source's
    // view of it, and must survive a bind being down. Each tab resolves its own.
    const { entity: brand } = useEntityAcrossSources({
        id: brandId,
        entityType: 'brands',
        dataSource,
    });
    const { name, type } = brand || {};
    const country = useCurrentCountry();
    const isNotTikTokMarket =
        country && !TIKTOK_COUNTRIES.includes(country.text_id);

    return (
        <PagePadding>
            <PageBar />
            <Stack $space="1.125rem">
                <DimensionHelmet name={name} type={type} />
                <Stack $space="1.25rem">
                    <div css="display: flex; justify-content: center;">
                        <TabList>
                            {!isNotTikTokMarket ? (
                                <TabButtonLink
                                    intent={
                                        mode === modes.OVERVIEW
                                            ? 'primary'
                                            : 'secondary'
                                    }
                                    to={mutateToWithCountryParam(
                                        `/brands/${brandId}`,
                                    )}
                                    mixpanelContext="OverviewTab"
                                >
                                    {labels.OVERVIEW}
                                </TabButtonLink>
                            ) : null}
                            <TabButtonLink
                                intent={
                                    mode === modes.SEARCH
                                        ? 'primary'
                                        : 'secondary'
                                }
                                to={mutateToWithCountryParam(
                                    `/brands/${brandId}/google`,
                                )}
                                mixpanelContext="SearchTab"
                            >
                                {labels.SEARCH}
                            </TabButtonLink>
                            <TabButtonLink
                                intent={
                                    mode === modes.TIKTOK
                                        ? 'primary'
                                        : 'secondary'
                                }
                                to={mutateToWithCountryParam(
                                    `/brands/${brandId}/tiktok`,
                                )}
                                mixpanelContext="TikTokTab"
                            >
                                {labels.TIKTOK}
                            </TabButtonLink>
                            <TabButtonLink
                                intent={
                                    mode === modes.INSTAGRAM
                                        ? 'primary'
                                        : 'secondary'
                                }
                                to={mutateToWithCountryParam(
                                    `/brands/${brandId}/instagram`,
                                )}
                                mixpanelContext="InstagramTab"
                            >
                                <Stack $space=".25rem" $direction="row">
                                    <span>{labels.INSTAGRAM}</span>
                                </Stack>
                            </TabButtonLink>
                        </TabList>
                    </div>
                    {mode === modes.OVERVIEW ? (
                        <OverviewBrand brandId={brandId} />
                    ) : null}
                    {mode === modes.SEARCH ? (
                        <SearchBrand
                            brandId={brandId}
                            controller={controller}
                        />
                    ) : null}
                    {mode === modes.TIKTOK ? (
                        <IfHasTikTokAccess>
                            <TikTokBrand
                                brandId={brandId}
                                controller={controller}
                            />
                        </IfHasTikTokAccess>
                    ) : null}
                    {mode === modes.INSTAGRAM ? (
                        <InstagramBrand
                            brandId={brandId}
                            controller={controller}
                        />
                    ) : null}
                </Stack>
            </Stack>
        </PagePadding>
    );
}

export default Brand;
